import { useState } from "react";
import { ChevronLeft, ChevronRight, ClipboardList, Save, Search, X, CheckCircle2, Hourglass } from "lucide-react";
import { toast } from "sonner";
import { StepBien } from "./StepBien";
import { StepRevenus } from "./StepRevenus";
import { StepCharges } from "./StepCharges";
import { StepFinancement } from "./StepFinancement";
import { SummaryPanel } from "./SummaryPanel";
import { SaveModal } from "./SaveModal";
import { trackEvent, type AnalyticsEvent } from "@/lib/analytics";
import type { ComputedResults, SimulationInput } from "../../lib/types";

interface WizardProps {
  input: SimulationInput;
  update: <K extends keyof SimulationInput>(k: K, v: SimulationInput[K]) => void;
  updateDetail: <K extends keyof SimulationInput["chargesDetail"]>(k: K, v: number) => void;
  results: ComputedResults;
  onSubmit: () => void;
  initialStep?: number;
}

const STEP_TOASTS: Record<number, string> = {
  1: "Bien noté, continuons !",
  2: "Super, ces revenus s'annoncent intéressants",
  3: "On y voit plus clair !",
  4: "Je calcule votre diagnostic...",
};

export function Wizard({ input, update, updateDetail, results, onSubmit, initialStep = 1 }: WizardProps) {
  const [step, setStep] = useState(initialStep);
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [recapOpen, setRecapOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const total = 4;

  const showStepToast = (s: number) => {
    toast.success(STEP_TOASTS[s] ?? "", {
      duration: 1500,
      className: "rentamnp-step-toast",
    });
  };

  const next = () => {
    showStepToast(step);
    trackEvent(`step_${step}_completed` as AnalyticsEvent);
    if (step < total) {
      setDirection("next");
      setStep((s) => s + 1);
    } else {
      setTimeout(() => onSubmit(), 400);
    }
  };
  const prev = () => {
    if (step > 1) {
      setDirection("prev");
      setStep((s) => s - 1);
    }
  };

  const animClass =
    direction === "next" ? "animate-slide-in-right" : "animate-slide-in-left";

  const isLast = step === total;

  return (
    <section id="wizard" className="mx-auto w-full max-w-6xl px-6 py-16 lg:px-12">
      <div className="flex items-start gap-8">
        <div className="mx-auto w-full max-w-2xl flex-1 lg:mx-0">
          {/* Bouton sauvegarder (au-dessus de la card, dès l'étape 2) */}
          {step >= 2 && (
            <div className="mb-3 flex justify-end">
              <button
                type="button"
                onClick={() => setSaveOpen(true)}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition hover:text-primary"
              >
                <Save className="h-3.5 w-3.5" />
                Sauvegarder et reprendre plus tard
              </button>
            </div>
          )}

          {/* Progress + bouton récap mobile */}
          <div className="mb-8">
            <div className="mb-2 flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-foreground">Étape {step} sur {total}</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setRecapOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs font-medium text-muted-foreground shadow-sm transition hover:border-primary/40 hover:text-foreground lg:hidden"
                  aria-label="Voir le récapitulatif"
                >
                  <ClipboardList className="h-3.5 w-3.5" />
                  Voir le récap
                </button>
                <span className="text-muted-foreground">{Math.round((step / total) * 100)}%</span>
              </div>
            </div>
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-primary-dark transition-all duration-500 ease-out"
                style={{ width: `${(step / total) * 100}%` }}
              />
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
            <div key={step} className={animClass}>
              {step === 1 && (
                <StepBien input={input} update={update} total={results.investissementTotal} />
              )}
              {step === 2 && (
                <StepRevenus
                  input={input}
                  update={update}
                  revenusAnnuels={results.revenusBrutsAnnuels}
                  revenusMensuels={results.revenusBrutsMensuels}
                />
              )}
              {step === 3 && (
                <StepCharges
                  input={input}
                  update={update}
                  updateDetail={updateDetail}
                  chargesTotales={results.chargesTotales}
                  revenusAnnuels={results.revenusBrutsAnnuels}
                />
              )}
              {step === 4 && (
                <StepFinancement
                  input={input}
                  update={update}
                  mensualite={results.mensualiteCredit}
                  coutInterets={results.coutTotalInterets}
                />
              )}
            </div>

            <div className="mt-8 flex items-center justify-between gap-3 border-t border-border pt-6">
              <button
                type="button"
                onClick={prev}
                disabled={step === 1}
                className="inline-flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition disabled:opacity-30 disabled:cursor-not-allowed enabled:hover:bg-muted enabled:hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
                Précédent
              </button>
              <button
                type="button"
                onClick={next}
                style={{ padding: "14px 32px", fontSize: "16px" }}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary font-semibold text-primary-foreground shadow-md transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-primary-dark hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                {isLast && <Search className="h-4 w-4" aria-hidden />}
                <span>{isLast ? "Voir mon diagnostic" : "Continuer"}</span>
                {!isLast && <ChevronRight className="h-4 w-4" aria-hidden />}
              </button>
            </div>
          </div>
        </div>

        <SummaryPanel input={input} results={results} step={step} />
      </div>

      {/* Bottom-sheet récap mobile */}
      {recapOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Récapitulatif de votre simulation"
        >
          <div
            className="absolute inset-0 bg-black/40 animate-fade-in-up"
            onClick={() => setRecapOpen(false)}
          />
          <div className="absolute inset-x-0 bottom-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-background p-5 shadow-2xl animate-slide-down">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground"><ClipboardList className="h-4 w-4 text-primary" aria-hidden />Votre simulation</h3>
              <button
                type="button"
                onClick={() => setRecapOpen(false)}
                className="rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Fermer"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <SummaryPanel input={input} results={results} step={step} variant="embedded" />
          </div>
        </div>
      )}

      <SaveModal open={saveOpen} onOpenChange={setSaveOpen} input={input} />
    </section>
  );
}
